import React, { useState, useEffect, useRef } from 'react';
import { sendingIntelligenceAPI } from '../services/api';

const VERDICT_LABEL = {
  recommended: 'Recommended',
  already_ramping: 'Already ramping',
  not_needed: 'Not needed',
  optional: 'Optional',
};

const plural = (n, one, many) => (n === 1 ? one : many);

function SendingAdviceCard({ fromEmail, ready = false, groupId, rampLocked, rampEnabled, showGroup = true, onApply, onAdvice }) {
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [noSender, setNoSender] = useState(false);

  // Only a real address (has '@') is sent; otherwise the server uses the Settings sender.
  const hasFrom = typeof fromEmail === 'string' && fromEmail.includes('@');
  const active = !!ready || hasFrom;

  // Keep the latest callback in a ref so the request effect's deps stay unchanged.
  const onAdviceRef = useRef(onAdvice);
  onAdviceRef.current = onAdvice;
  const notifyAdvice = (a) => { if (onAdviceRef.current) onAdviceRef.current(a); };

  useEffect(() => {
    if (!active) {
      setAdvice(null);
      notifyAdvice(null);
      setLoading(false);
      setFailed(false);
      setNoSender(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setNoSender(false);
    const payload = { group_id: groupId ? parseInt(groupId, 10) : undefined };
    if (hasFrom) payload.from_email = fromEmail;
    sendingIntelligenceAPI
      .getAdvice(payload)
      .then((res) => {
        if (cancelled) return;
        setAdvice(res.data.advice);
        notifyAdvice(res.data.advice);
      })
      .catch((err) => {
        if (cancelled) return;
        setAdvice(null);
        notifyAdvice(null);
        if (err?.response?.data?.error_type === 'no_sender') setNoSender(true);
        else setFailed(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, hasFrom, fromEmail, groupId]);

  if (!active) return null;

  const boxStyle = { border: '1px solid var(--primary-soft-hover)', background: 'var(--primary-soft)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem' };

  if (loading) return <div style={boxStyle}>{hasFrom ? `Checking ${fromEmail}…` : 'Checking your sending setup…'}</div>;
  if (noSender) return <div style={{ ...boxStyle, borderColor: 'rgba(220, 38, 38, 0.2)', background: 'var(--danger-soft)', color: 'var(--danger)' }}>No sender email is configured. Set a Sender email in Settings — without one the ramp can't work and emails can't be sent.</div>;
  if (failed) return <div style={{ ...boxStyle, borderColor: 'var(--border-color)', background: 'var(--surface-subtle)' }}>Couldn't check this address right now. You can still set the options below.</div>;
  if (!advice) return null;

  const { domain, ramp, group, prioritize } = advice;
  const laterDays = group ? Math.max(group.days_to_finish - 1, 1) : 1;
  const groupTotal = group ? (group.total_in_group ?? group.size) : 0;
  const skippedDnc = group?.skipped_dnc || 0;

  // null = leave that toggle as the user has it
  const rampChange = rampLocked ? null
    : ramp.verdict === 'recommended' ? true : ramp.verdict === 'not_needed' ? false : null;
  const prioritizeChange = prioritize.verdict === 'recommended' ? true
    : prioritize.verdict === 'not_needed' ? false : null;
  const nothingToApply = rampChange === null && prioritizeChange === null;

  const handleApply = () => onApply({ ramp: rampChange, prioritize: prioritizeChange });

  return (
    <div style={boxStyle}>
      <strong>Suggestions for {advice.from_email || fromEmail}</strong>
      {advice.from_email_source === 'settings' && (
        <span style={{ marginLeft: '0.4rem', color: 'var(--text-secondary)' }}>(your default sender from Settings)</span>
      )}

      {domain.blockers.map((b) => (
        <p key={b.check} style={{ margin: '0.4rem 0 0', color: 'var(--danger)' }}>
          ⚠ {b.check.toUpperCase()}: {b.summary} {b.action ? `— ${b.action}` : ''}
        </p>
      ))}
      {!domain.checked && <p style={{ margin: '0.4rem 0 0', color: 'var(--text-secondary)' }}>Domain DNS could not be checked just now.</p>}
      {domain.checked && domain.blockers.length === 0 && (
        <p style={{ margin: '0.4rem 0 0', color: 'var(--success)' }}>✓ No DNS problems found (SPF and DMARC).</p>
      )}

      <p style={{ margin: '0.4rem 0 0' }}>
        <strong>Ramp — {VERDICT_LABEL[ramp.verdict]}:</strong> {ramp.reason}
      </p>

      {showGroup && group && (
        <p style={{ margin: '0.4rem 0 0' }}>
          <strong>This group:</strong> {groupTotal} {plural(groupTotal, 'buyer', 'buyers')}
          {group.total_in_group !== undefined && group.total_in_group !== null
            ? `${skippedDnc > 0 ? `, ${skippedDnc} skipped (Do Not Contact)` : ''}, ${group.size} eligible`
            : ''}
          {group.size === 0 && '.'}
          {group.size > 0 && (
            <>. With ramp on: {group.sends_today} would send today{group.deferred > 0 ? `, ${group.deferred} over the next ${laterDays} ${plural(laterDays, 'day', 'days')}` : ''}
              {group.excluded > 0 ? `, ${group.excluded} excluded (rejected)` : ''}.
              {!rampLocked && !rampEnabled && ` With ramp off, all ${group.size - group.excluded} would send today.`}</>
          )}
        </p>
      )}

      <p style={{ margin: '0.4rem 0 0' }}>
        <strong>Prioritize — {VERDICT_LABEL[prioritize.verdict]}:</strong> {prioritize.reason}
      </p>

      <button type="button" onClick={handleApply} disabled={nothingToApply}
              style={{ marginTop: '0.6rem', color: 'var(--primary-hover)', background: 'none', border: '1px solid var(--primary-light)', borderRadius: '4px', padding: '0.25rem 0.75rem', cursor: nothingToApply ? 'default' : 'pointer', opacity: nothingToApply ? 0.5 : 1 }}>
        Apply suggestion
      </button>
      <small style={{ marginLeft: '0.5rem', color: 'var(--text-secondary)' }}>Sets the toggles below. You can still change them.</small>
    </div>
  );
}

export default SendingAdviceCard;
